import type { AlertProps } from '@mui/material/Alert';
import type { Theme } from '@mui/material/styles';

import { alertClasses } from '@mui/material/Alert';
import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

const COLORS = ['info', 'success', 'warning', 'error'] as const;

// ----------------------------------------------------------------------

export function alert(theme: Theme) {
  const lightMode = theme.palette.mode === 'light';

  const rootStyles = (ownerState: AlertProps) => {
    const standardVariant = ownerState.variant === 'standard';

    const filledVariant = ownerState.variant === 'filled';

    const outlinedVariant = ownerState.variant === 'outlined';

    const colorStyle = COLORS.map((color) => ({
      ...(ownerState.severity === color && {
        // STANDARD
        ...(standardVariant && {
          // `dark`, not `main`, on a light ladder: the icon sits on the
          // `lighter` tint, where `main` is 1.73:1 (warning — the past-due
          // billing banner) to 2.13:1 (info). `dark` clears 3:1 for all four.
          [`& .${alertClasses.icon}`]: {
            color: theme.palette[color][lightMode ? 'dark' : 'light'],
          },
          backgroundColor:
            theme.palette[color][lightMode ? 'lighter' : 'darker'],
          color: theme.palette[color][lightMode ? 'darker' : 'lighter'],
        }),
        // FILLED
        ...(filledVariant && {
          backgroundColor: theme.palette[color].main,
          color: theme.palette[color].contrastText,
        }),
        // OUTLINED
        ...(outlinedVariant && {
          [`& .${alertClasses.icon}`]: {
            color: theme.palette[color].main,
          },
          backgroundColor: alpha(theme.palette[color].main, 0.08),
          border: `solid 1px ${alpha(theme.palette[color].main, 0.16)}`,
          color: theme.palette[color][lightMode ? 'dark' : 'light'],
        }),
      }),
    }));

    return [...colorStyle];
  };

  return {
    MuiAlert: {
      styleOverrides: {
        icon: {
          opacity: 1,
        },
        root: ({ ownerState }: { ownerState: AlertProps }) =>
          rootStyles(ownerState),
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontWeight: theme.typography.fontWeightSemiBold,
          marginBottom: theme.spacing(0.5),
        },
      },
    },
  };
}
